/**
 * The index that says which transform produced which blob.
 *
 * Decision 0041. A derivative is content-addressed by its own bytes like
 * anything else in `blobs.ts`, and that is exactly why it needs a second
 * record: the digest of a thumbnail does not say what it is a thumbnail OF,
 * which decoder made it, or under what policy. So ingestion cannot ask "have I
 * already made this" without decoding the source again to find out, which is
 * the whole cost the derivative was supposed to avoid.
 *
 * The key is the causal fact:
 *
 *     (source blob id, transform, transform version, canonical parameters)
 *
 * `source` is already the digest of the complete source bytes, so it is the
 * source's identity and nothing further is needed to name it. `transform` is a
 * closed operation name — `image-thumbnail`, `video-poster-frame` — and is
 * NOT the semantic role: two transforms may produce the same role, so the role
 * lives in the value where it describes the result rather than in the key where
 * it would pretend to identify the operation.
 *
 * ## Why the version is in the key rather than derived from the output
 *
 * Decision 0041 states it and it is the one part of this that is easy to get
 * wrong cheaply: content-addressing the ENCODED OUTPUT deduplicates only
 * byte-identical results. A browser encoder update produces different WebP
 * bytes from the same pixels, and no amount of hashing the output tells you
 * which contract produced it. `transformVersion` changes whenever the decoder
 * choice, frame-selection policy, resize policy, encoder, quality or colour
 * handling changes — anything capable of changing the output. Two versions
 * pointing at the same derived blob is fine and expected; one version standing
 * for two different contracts is the defect this key exists to prevent.
 *
 * ## What is deliberately not here
 *
 * No lifecycle. Decision 0041 permits failed and in-progress records when retry
 * coordination or collection needs them, and requires neither: *"the minimum
 * persisted state is the completed mapping."* A failed decode publishes
 * nothing, so a miss and a past failure are the same thing to a reader and the
 * caller derives again. That is the honest v1 — an `in-progress` record
 * nothing ever reaps is a lock that outlives the tab that took it.
 */

import type { BlobId } from './blobs.js';

/** A value a parameter may take. Scalars only, and the restriction is load
 *  bearing rather than conservative: a canonical encoding of an arbitrary
 *  object graph has to decide about cycles, prototypes and `Date`, and a key
 *  that encodes two equal things differently silently doubles the work this
 *  index exists to avoid. */
export type DerivativeParameter = string | number | boolean;

export interface DerivativeKey {
  /** The digest of the complete source bytes. */
  readonly source: BlobId;
  /** A closed operation name. Not the role. */
  readonly transform: string;
  /** Bumped whenever anything capable of changing the output changes. */
  readonly transformVersion: number;
  /** Canonically encoded before it enters the key, so two spellings of the
   *  same parameters cannot address two mappings. */
  readonly parameters?: Readonly<Record<string, DerivativeParameter | undefined>>;
}

/** What the mapping names: the derived blob, plus enough to use and audit it
 *  without opening the bytes. */
export interface DerivativeRecord {
  readonly derived: BlobId;
  /** The semantic role a lens binds to — `media.thumb`, `media.poster-frame`.
   *  Metadata on the result, because two transforms may produce one role. */
  readonly role: string;
  readonly mime: string;
  readonly width: number;
  readonly height: number;
  readonly duration?: number;
}

/** A completed mapping and the key it answers, for `all()`. */
export interface DerivativeEntry {
  readonly key: DerivativeKey;
  readonly record: DerivativeRecord;
}

/**
 * Two different completed mappings for one key.
 *
 * Decision 0041: *"a conflicting completed mapping for the same key is an
 * integrity error rather than last-writer-wins."* Last-writer-wins would make
 * this index a cache with no way to notice it had started lying — the second
 * writer's result is not more true than the first's, and if they disagree then
 * one of them derived under a contract that is not the one the key names.
 *
 * Republishing an IDENTICAL record is not a conflict; concurrent derivations of
 * one key are permitted to duplicate work and must converge, and this is what
 * converging looks like.
 */
export class DerivativeConflict extends Error {
  constructor(
    readonly key: string,
    readonly held: DerivativeRecord,
    readonly offered: DerivativeRecord,
  ) {
    super(
      `two different derivatives published for ${key}: `
      + `${held.derived} (${held.role}) and ${offered.derived} (${offered.role})`,
    );
    this.name = 'DerivativeConflict';
  }
}

/**
 * The one place the key's spelling is decided.
 *
 * Sorted parameter names, `JSON.stringify` for names and values so a string
 * containing a delimiter cannot forge one, and the four parts joined by
 * characters no part can reach. Exported because a backend has to store the
 * encoded form and a test has to be able to assert two objects agree on it --
 * and because a key encoded in two places is a key two layers disagree about,
 * which is the rule `blobRef`/`blobIdIn` already follow one file over.
 *
 * A parameter whose value is `undefined` is dropped rather than encoded, which
 * is what `JSON.stringify` does with one and therefore what a caller will
 * assume. `{ quality: undefined }` and `{}` are the same request.
 */
export function derivativeKeyOf(key: DerivativeKey): string {
  if (!key.transform) throw new Error('a derivative key needs a transform name');
  if (!Number.isInteger(key.transformVersion) || key.transformVersion < 0) {
    throw new Error(
      `transform version must be a non-negative integer, got ${String(key.transformVersion)}`,
    );
  }
  const entries = Object.entries(key.parameters ?? {})
    .filter((pair): pair is [string, DerivativeParameter] => pair[1] !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  for (const [name, value] of entries) {
    const kind = typeof value;
    if (kind !== 'string' && kind !== 'number' && kind !== 'boolean') {
      throw new Error(`derivative parameter ${name} must be a scalar, got ${kind}`);
    }
    if (typeof value === 'number' && !Number.isFinite(value)) {
      throw new Error(`derivative parameter ${name} must be finite, got ${String(value)}`);
    }
  }
  const parameters = entries
    .map(([name, value]) => `${JSON.stringify(name)}:${JSON.stringify(value)}`)
    .join(',');
  return `${key.source} ${key.transform} ${key.transformVersion} {${parameters}}`;
}

/** True when two mappings say the same thing. `undefined` and an absent
 *  `duration` are the same absence, which is a distinction a structured clone
 *  through IndexedDB is not obliged to preserve. */
export function sameDerivative(a: DerivativeRecord, b: DerivativeRecord): boolean {
  return a.derived === b.derived && a.role === b.role && a.mime === b.mime
    && a.width === b.width && a.height === b.height
    && (a.duration ?? undefined) === (b.duration ?? undefined);
}

/**
 * The index itself.
 *
 * A member of `BlobStore` rather than a sibling, because decision 0041 puts it
 * *"at the blob-store boundary"* and because `lookup` cannot answer correctly
 * without asking whether the derived blob is still there. An index that can be
 * held apart from the bytes it names is an index that can be right about a blob
 * somebody deleted.
 */
export interface DerivativeIndex {
  /**
   * The completed mapping, or `undefined` — including when a mapping exists
   * but its derived blob does not. Decision 0041: *"an index hit whose derived
   * blob is absent is stale and behaves as a miss."* The caller derives again,
   * which is the repair; nothing here deletes the stale row, because the
   * publish that follows replaces it and a delete on the read path is a write
   * nobody asked for.
   */
  lookup(key: DerivativeKey): Promise<DerivativeRecord | undefined>;
  /**
   * Record that this transform contract, applied to this source, produced this
   * blob. Idempotent for an identical record; throws `DerivativeConflict` when
   * a DIFFERENT record is already held for the key and that record's blob is
   * still present.
   */
  publish(key: DerivativeKey, record: DerivativeRecord): Promise<void>;
  /** Drop one mapping. The explicit retention operation decision 0041 names:
   *  *"deleting an old transform version is an explicit retention operation,
   *  never an incidental effect of ingesting with a newer version."* */
  forget(key: DerivativeKey): Promise<void>;
  /** Every mapping held, stale ones included. The floor under any future
   *  collection, and the only way to ask what a source has been derived into
   *  without knowing the versions in advance. */
  all(): Promise<readonly DerivativeEntry[]>;
}

/**
 * The half of the index a backend has to provide: rows, by encoded key.
 *
 * Split out so the staleness rule and the conflict rule are written ONCE and
 * both backends get the same ones. Decision 0041 requires memory and IndexedDB
 * to *"expose the same contract today"*, and two implementations of a rule are
 * two chances to implement it differently — which is precisely what an
 * integrity error is there to catch.
 */
export interface DerivativeRows {
  get(encoded: string): Promise<DerivativeEntry | undefined>;
  /**
   * Write the row, but ONLY if it still holds what the caller compared against.
   *
   * @Codex, P1: *"both calls can complete `rows.get(encoded)` before either
   * reaches `rows.put`, so both observe no held mapping and the later write
   * silently wins. IndexedDB makes this especially direct because `get` and
   * `put` use separate transactions … this bypasses `DerivativeConflict`."*
   *
   * Correct, and it is decision 0041's own requirement that a conflicting
   * completion be an integrity error rather than last-writer-wins. A plain
   * `put` cannot honour that no matter what the rules above it say: the
   * comparison and the write have to be one indivisible step, so this is the
   * only write the interface offers.
   *
   * `expected` is the WHOLE mapping that was read, or `undefined` for *"there
   * was no row"*. Returns `false` when the row has since changed — the caller
   * re-reads and re-decides, which is where the conflict is then raised against
   * the mapping that actually won.
   *
   * `expectedPresent` is what the caller observed about the derived blob when
   * it decided, and the backend re-checks it **inside the same atomic step**.
   * @Codex, P2 on #184: *"When publisher A sees the held derivative blob as
   * absent, another ingest can restore those content-addressed bytes before A
   * reaches `putIf`; because the mapping row itself has not changed, the CAS
   * succeeds and overwrites what is now a valid conflicting mapping without
   * raising `DerivativeConflict`."*
   *
   * The row was the only thing being guarded and the DECISION rested on two
   * facts. A stale mapping is defined by its blob being gone, so a blob coming
   * back turns a repair into an overwrite — and content addressing makes that
   * likelier than it sounds, because any other ingest of the same picture
   * restores exactly those bytes.
   *
   * The whole record rather than its `derived` id, which is what it compared
   * first. @Codex, P2 on #184: *"If a publisher reads mapping A while a
   * concurrent explicit `forget()` and republish replaces it with mapping B
   * that points to the same blob but has different role, MIME, dimensions, or
   * duration, this comparison succeeds and silently overwrites B instead of
   * re-reading and raising `DerivativeConflict`."*
   *
   * A textbook ABA, and the blob id is exactly the wrong token for it: decision
   * 0041 says two transform versions may legitimately point at one derived
   * blob, so the id is the field most likely to be equal across two genuinely
   * different mappings.
   */
  putIf(
    encoded: string, expected: DerivativeRecord | undefined,
    expectedPresent: boolean, entry: DerivativeEntry,
  ): Promise<boolean>;
  delete(encoded: string): Promise<void>;
  all(): Promise<readonly DerivativeEntry[]>;
}

/** How many times `publish` will re-read a row that changed under it before
 *  giving up. Bounded rather than a `while`: a caller that spins forever on a
 *  row somebody else keeps rewriting is a hang, and this file exists in a
 *  codebase that has already paid for one of those (task 0112). */
const PUBLISH_ATTEMPTS = 8;

/** Whether a blob is still there. `BlobStore['stat']` narrowed to the one
 *  question this file asks, so the index can be built before the store literal
 *  that will hold it is complete. */
export type BlobPresence = (id: BlobId) => Promise<unknown | undefined>;

/** The rules, over any backend's rows. */
export function derivativeIndexOver(
  rows: DerivativeRows,
  present: BlobPresence,
): DerivativeIndex {
  return {
    async lookup(key) {
      /**
       * Re-read after the probe, and return only what was current for both.
       *
       * @Codex, P2 on #184: *"When a lookup of stale mapping A races a repair to
       * mapping B and another ingest restores A's content-addressed bytes,
       * `rows.get()` can observe A, the repair can replace the row with B, and
       * the later `present(A)` probe then succeeds. This returns A even though
       * there was no point during the lookup when A was both the current
       * mapping and present."*
       *
       * The probe cannot join the row's transaction — it reads a different
       * store through a different door, which `publish` already says. So the
       * row is read on both sides of it and the answer is used only if it did
       * not move. One extra read on the hit path, and a miss on the race, which
       * costs a decode and cannot attach the losing derivative.
       */
      const encoded = derivativeKeyOf(key);
      const held = await rows.get(encoded);
      if (!held) return undefined;
      const there = await present(held.record.derived);
      if (!there) return undefined;
      const still = await rows.get(encoded);
      if (!still || !sameDerivative(still.record, held.record)) return undefined;
      return held.record;
    },

    async publish(key, record) {
      const encoded = derivativeKeyOf(key);
      /**
       * The offered record is SNAPSHOT before anything yields.
       *
       * @Codex, P2 on #184: *"When an IndexedDB publisher mutates its offered
       * `record` after the conflict comparison but while `rows.putIf()` is
       * awaiting its presence/current-row requests, `entry` still references
       * that same object, so the eventual structured clone persists the
       * mutated mapping. An offer initially identical to held mapping A can
       * therefore become conflicting mapping B and overwrite A without
       * `DerivativeConflict`; the memory backend instead clones synchronously
       * and does not expose this window."*
       *
       * The compare-and-set is what makes it reachable: `publish` deliberately
       * awaits twice between reading the row and writing it, because the
       * presence probe cannot join the write's transaction. Every one of those
       * awaits is a turn in which the caller's object is still theirs.
       *
       * `readonly` on `DerivativeRecord` is not a runtime guarantee — the same
       * sentence `revise.ts` and `blobs.ts` each learned from a review this
       * week. The copy is the guarantee.
       *
       * Cloned rather than spread so a record that grows a nested field later
       * cannot reopen this quietly; it is six scalars today and the cost is
       * nothing beside the two round trips below.
       */
      const offered = structuredClone(record);
      const entry = { key: normalizedKey(key), record: offered };
      // Read, decide, write-if-unchanged, and go round again if it moved. The
      // presence probe cannot live inside the write's transaction — it reads a
      // different store through a different door — so the decision is made
      // outside and the write refuses to land on a row that has changed since.
      for (let attempt = 0; attempt < PUBLISH_ATTEMPTS; attempt += 1) {
        const held = await rows.get(encoded);
        // Only a mapping whose blob is still THERE can conflict. One whose blob
        // is gone was already a miss to every reader, so the derivation that
        // replaces it is the repair rather than a second opinion.
        const there = held ? Boolean(await present(held.record.derived)) : false;
        // The SNAPSHOT decides, and the snapshot is what gets written — the
        // two must be the same object or the comparison is about a value the
        // write may no longer be storing.
        if (held && !sameDerivative(held.record, offered) && there) {
          throw new DerivativeConflict(encoded, held.record, offered);
        }
        // BOTH facts the decision rested on go into the write, or a blob that
        // came back between the two turns a repair into an overwrite.
        if (await rows.putIf(encoded, held?.record, there, entry)) return;
      }
      throw new Error(
        `the derivative mapping for ${encoded} changed under ${PUBLISH_ATTEMPTS} `
        + 'attempts to publish it',
      );
    },

    async forget(key) { await rows.delete(derivativeKeyOf(key)); },
    all() { return rows.all(); },
  };
}

/** The key as it is stored beside its record, with `undefined` parameters
 *  dropped so a row read back describes the key that actually addressed it.
 *  Sorting is not needed here: the encoded string is the identity and this is
 *  the readable copy beside it. */
function normalizedKey(key: DerivativeKey): DerivativeKey {
  /**
   * `Object.fromEntries`, not assignment into a literal.
   *
   * @Codex, P2 on #184: *"For a valid derivative key containing an own
   * `__proto__` parameter, assigning into this ordinary object invokes the
   * legacy prototype setter and creates no own property. The row is stored under
   * an encoded key that includes the parameter, but `all()` returns a normalized
   * key without it, so passing that returned key to `forget()` deletes a
   * different address and leaves the mapping behind."*
   *
   * Which is the one operation `all()` exists to feed. `derivativeKeyOf` reads
   * `Object.entries`, so the ENCODED key has the parameter and the readable copy
   * beside it did not — two spellings of one mapping, from a name a browser
   * treats specially and a `Record<string, …>` type permits.
   */
  const parameters = Object.fromEntries(
    Object.entries(key.parameters ?? {})
      .filter((pair): pair is [string, DerivativeParameter] => pair[1] !== undefined),
  );
  return {
    source: key.source,
    transform: key.transform,
    transformVersion: key.transformVersion,
    parameters,
  };
}

/** Rows in a `Map`, for `createMemoryBlobs` and for a test that wants the
 *  contract without a database. */
/**
 * Whether a blob is there, answered WITHOUT yielding.
 *
 * The memory backend's atomicity is "no `await` between the comparison and the
 * write", and an async probe breaks that by definition. Separate from
 * `BlobPresence` because the two are used at different layers for different
 * reasons: the index's probe may go to a database, and this one may not.
 */
export type SyncBlobPresence = (id: BlobId) => boolean;

export function memoryDerivativeRows(present: SyncBlobPresence = () => true): DerivativeRows {
  const held = new Map<string, DerivativeEntry>();
  /**
   * Cloned in and out, the way `createMemoryBlobs` copies its bytes and the
   * genome store clones its projections.
   *
   * @Codex, P2 on #184: *"When a caller publishes a mutable object and later
   * changes one of its fields, this `Map` retains the same object reference, so
   * the completed mapping changes without another `publish()` call or any
   * conflict check … IndexedDB structured-clones the same input, so the two
   * required backends expose different integrity behavior."*
   *
   * The last clause is the one that matters: decision 0041 requires both
   * backends to expose the SAME contract, and a mapping that can be edited
   * after publication in one of them and not the other is two contracts. The
   * `readonly` on the parameter does not stop it — a mutable object is
   * structurally assignable to a readonly type, which is a compile-time
   * courtesy rather than a guarantee about the object.
   */
  const own = (entry: DerivativeEntry): DerivativeEntry => structuredClone(entry);
  return {
    async get(encoded) {
      const row = held.get(encoded);
      return row === undefined ? undefined : own(row);
    },
    // Atomic because there is no `await` between the read and the write: a
    // JavaScript function body runs to completion, so no other publish can
    // interleave here. That is a property of this backend rather than a rule —
    // which is exactly why the interface asks for the guarantee instead of
    // asking each backend to be careful.
    async putIf(encoded, expected, expectedPresent, entry) {
      /**
       * Every check and the write in ONE synchronous run.
       *
       * @Codex, P2 on #184: *"`await present(...)` records absence and yields
       * before the row comparison and write; another ingest can restore the
       * content-addressed blob during that yield, after which this call still
       * overwrites the now-valid conflicting mapping … the new IndexedDB
       * implementation checks presence and the row in one transaction, but this
       * memory implementation still has a promise boundary between its presence
       * decision and write, so the two required backends do not provide the same
       * atomic conflict behavior."*
       *
       * The comment this replaces said *"no `await` between them"* and had one,
       * put there by the commit that added the presence check. A JavaScript
       * function body is atomic only until it yields, so an `await` inside it is
       * exactly the thing the claim excludes — which makes the probe's SIGNATURE
       * the fix rather than its position.
       *
       * **Structural, and not covered by a clause that can go red.** Two
       * attempts to write one failed: a microtask flipping presence lands after
       * the probe in either shape, so the value decided on is the same and the
       * write is correct both times. The window this closes needs a competing
       * operation to change presence BETWEEN the read and the write, which a
       * test can only schedule as a microtask — the one thing that cannot land
       * there. `packages/store/test/derivatives.test.ts` says so where it pins
       * the compare-and-set, rather than claiming that clause as this guard.
       */
      const there = expected ? present(expected.derived) : false;
      if (expected && there !== expectedPresent) return false;
      const now = held.get(encoded)?.record;
      // Both absent, or both present and equal in every field. `sameDerivative`
      // rather than `===`, because the row read back is a different object.
      if ((now === undefined) !== (expected === undefined)) return false;
      if (now && expected && !sameDerivative(now, expected)) return false;
      held.set(encoded, own(entry));
      return true;
    },
    async delete(encoded) { held.delete(encoded); },
    async all() { return [...held.values()].map(own); },
  };
}
